/**
 * ClassRegistry - maps string identifiers to constructors for dynamic object creation and serialization.
 */

import * as THREE from 'three';

export enum Prefix {
  ENGINE = 'ENGINE.',
  GAME = 'GAME.',
  THREE = 'THREE.',
}

/**
 * Represents a class constructor for T or any subclass of T.
 * Useful for APIs that accept a class reference to instantiate.
 */

export type Constructor<T = any> = (new (...args: any[]) => T) & { prototype: T };
export type AbstractConstructor<T = any> = (abstract new (...args: any[]) => T) & { prototype: T };

function isConstructor(fn: any): fn is Constructor {
  return typeof fn === 'function' && fn.prototype && fn.prototype.constructor === fn;
}

/**
 * Gets the constructor (class) of an object instance.
 * Provides proper typing for the .constructor property which TypeScript
 * otherwise types as the generic `Function`.
 */
export function getClassOf<T>(obj: T): Constructor<T> {
  return (obj as { constructor: Constructor<T> }).constructor;
}

/** Entry describing a registered WebGPU node-material asset class. */
export interface NodeMaterialAssetEntry {
  ctor: Constructor;
  /** Human-readable display name shown in the editor dialog. */
  displayName: string;
  /** Optional grouping label for the editor combobox (e.g. 'FX Effects'). */
  group?: string;
  /** Whether the class was registered as an engine or game class. */
  source: 'engine' | 'game';
  /**
   * When true, omit from the editor "Create New Material" type list (demo-only / internal assets).
   * Serialization and the property inspector still resolve the class normally.
   */
  excludeFromNewMaterialDialog?: boolean;
}

export class ClassRegistry {
  private static registry: Map<string, Constructor> = new Map();
  private static reverseRegistry: Map<Constructor, string> = new Map();
  private static builtInClassesRegistered = false;
  private static redirectorMap: Map<string, string> = new Map();  // 'Engine.OldClass -> Engine.NewClass'
  private static resourceClasses: Set<Constructor> = new Set();  // Classes marked as resources
  private static resourcePrefixes: Map<Constructor, string> = new Map();  // Resource file prefixes (e.g., 'material', 'res')
  private static abstractClasses: Set<Constructor> = new Set();  // Abstract classes registered for type-checking only
  private static nodeMaterialAssetEntries: Map<Constructor, NodeMaterialAssetEntry> = new Map();

  /**
   * Registers a class as an engine component.
   * @param constructor - The class constructor to register
   */
  public static registerEngineClass(constructor: Constructor, className?: string): void {
    this.registerClass(Prefix.ENGINE, constructor, className);
  }

  /**
   * Registers a class as a game component.
   * @param constructor - The class constructor to register
   */
  public static registerGameClass(constructor: Constructor): void {
    this.registerClass(Prefix.GAME, constructor);
  }


  /**
   * Registers an abstract engine class for type-checking purposes only.
   * The class will be resolvable via getClassByConstructorName/getClass but
   * constructObject will throw if called on it.
   */
  public static registerAbstractEngineClass(constructor: AbstractConstructor, className?: string): void {
    this.registerClass(Prefix.ENGINE, constructor as unknown as Constructor, className);
    this.abstractClasses.add(constructor as unknown as Constructor);
  }

  /**
   * Returns true if the given constructor was registered as abstract.
   */
  public static isAbstractClass(constructor: Constructor | AbstractConstructor): boolean {
    return this.abstractClasses.has(constructor as Constructor);
  }

  public static addClassesToRedirect(classes: Record<string, string>) {
    for (const [k, v] of Object.entries(classes)) {
      this.redirectorMap.set(k, v);
    }
  }

  /** @returns The registry as a readonly map */
  public static getRegistry(): ReadonlyMap<string, Constructor> {
    return this.registry;
  }

  /**
   * Marks a class constructor as a resource.
   * @param prefix - The file prefix for this resource type (defaults to 'res')
   */
  public static markAsResource(constructor: Constructor, prefix: string = 'res'): void {
    this.resourceClasses.add(constructor);
    this.resourcePrefixes.set(constructor, prefix);
  }

  /** @returns True if the constructor is marked as a resource */
  public static isResourceClass(constructor: Constructor): boolean {
    return this.resourceClasses.has(constructor);
  }

  /** @returns The resource prefix (e.g., 'material', 'res') or undefined if not a resource */
  public static getResourcePrefix(constructor: Constructor): string | undefined {
    return this.resourcePrefixes.get(constructor);
  }

  /**
   * Marks a constructor as a serializable WebGPU node-material asset.
   * By default these classes appear in the NewMaterialDialog; set
   * `excludeFromNewMaterialDialog` on the decorator to omit demo-only types.
   */
  public static markAsNodeMaterialAsset(
    constructor: Constructor,
    displayName: string,
    source: 'engine' | 'game',
    group?: string,
    excludeFromNewMaterialDialog?: boolean,
  ): void {
    this.nodeMaterialAssetEntries.set(constructor, {
      ctor: constructor,
      displayName,
      group,
      source,
      ...(excludeFromNewMaterialDialog ? { excludeFromNewMaterialDialog: true } : {}),
    });
  }

  /** @returns True if the constructor is a registered node-material asset class. */
  public static isNodeMaterialAssetClass(constructor: Constructor): boolean {
    if (this.nodeMaterialAssetEntries.has(constructor)) return true;
    // Also check prototype chain so subclasses are recognized.
    let proto = Object.getPrototypeOf(constructor);
    while (proto && proto !== Function.prototype) {
      if (this.nodeMaterialAssetEntries.has(proto)) return true;
      proto = Object.getPrototypeOf(proto);
    }
    return false;
  }

  /** @returns All registered node-material asset class entries. */
  public static getNodeMaterialAssetClasses(): readonly NodeMaterialAssetEntry[] {
    return Array.from(this.nodeMaterialAssetEntries.values());
  }

  /**
   * Retrieves a class constructor by its full name.
   * @throws Error if the class is not found in the registry
   */
  public static getClass<T = any>(fullName: string): Constructor<T> {
    this.registerBuiltInClasses();
    fullName = this.redirectorMap.get(fullName) ?? fullName;
    const classConstructor = this.registry.get(fullName);
    if (!classConstructor) {
      throw new Error(`Class ${fullName} not found in registry`);
    }
    return classConstructor;
  }

  /** Removes all game classes from the registry. Used when reloading game code. */
  public static clearGameClasses(): void {
    for (const [key, value] of this.registry.entries()) {
      if (key.startsWith(Prefix.GAME)) {
        this.registry.delete(key);
        this.reverseRegistry.delete(value);
      }
    }
    for (const [constructor, entry] of this.nodeMaterialAssetEntries.entries()) {
      if (entry.source === 'game') {
        this.nodeMaterialAssetEntries.delete(constructor);
      }
    }
  }

  /**
   * Creates a new instance of a registered class.
   * @param className - The full name of the class to instantiate
   * @param args - Arguments to pass to the constructor
   * @returns A new instance of the specified class
   */
  public static constructObject(className: string, ...args: any[]): any {
    const constructor = this.getClass(className);
    if (this.abstractClasses.has(constructor)) {
      throw new Error(`Cannot instantiate abstract class ${className}`);
    }
    if (args.length === 1) {
      const firstArg = args[0];
      if (Array.isArray(firstArg)) {
        // Support: constructObject('Foo', [1, 2, 3])
        return new constructor(...firstArg);
      }
      if (typeof firstArg === 'object') {
        // Support: constructObject('Foo', { config: 123 })

        // Check if this is a THREE.js class - they don't accept unknown properties
        if (className.startsWith(Prefix.THREE)) {
          return new constructor(firstArg);
        }

        const object =
          'create' in constructor && typeof constructor.create === 'function'
            ? constructor.create(firstArg)
            : new constructor(firstArg);
        return object;
      }
    }
    return new constructor(...args);
  }

  /** Retrieves the class name of an object or its constructor. */
  public static getClassOrConstructorName(object: any): string {
    return this.getClassName(object)?.split('.').pop() ?? object.constructor.name;
  }

  /** Retrieves the full name of a class from its constructor. */
  public static getClassName(object: any): string | null {
    this.registerBuiltInClasses();

    // First try direct constructor lookup
    const directLookup = this.reverseRegistry.get(object.constructor);
    if (directLookup) return directLookup;

    // Fallback to name-based lookup if direct lookup fails
    const constructorName = object.constructor?.name;
    if (!constructorName) return null;

    // Search through registry for matching class name
    for (const [fullName, constructor] of this.registry.entries()) {
      if (constructor.name === constructorName) {
        // Update reverse registry for future lookups
        this.reverseRegistry.set(object.constructor, fullName);
        return fullName;
      }
    }
    return null;
  }

  /**
   * Retrieves the full name of a class from its constructor.
   * @throws Error if the class is not found in the registry
   */
  public static getClassNameChecked(object: any): string {
    const className = this.getClassName(object);
    if (!className) {
      throw new Error(`Class ${object.constructor.name} not found in registry`);
    }
    return className;
  }

  /**
   * Searches for a registered class by its constructor name.
   * @param constructorName - The name of the constructor to search for
   * @returns The full registered name (e.g., "ENGINE.MeshComponent") or null if not found
   */
  public static getClassNameByConstructorName(constructorName: string): string | null {
    this.registerBuiltInClasses();

    for (const registeredName of this.registry.keys()) {
      if (registeredName.endsWith(`.${constructorName}`) || registeredName === constructorName) {
        return registeredName;
      }
    }
    return null;
  }

  /**
   * Retrieves a class constructor by its constructor name.
   * Useful for hot reload scenarios where you need to find the fresh constructor.
   * @param constructorName - The name of the constructor to search for
   * @returns The class constructor or null if not found
   */
  public static getClassByConstructorName<T = any>(constructorName: string): Constructor<T> | null {
    const fullName = this.getClassNameByConstructorName(constructorName);
    if (!fullName) {
      return null;
    }
    return this.registry.get(fullName) as Constructor<T>;
  }

  /** Registers all built-in classes in the THREE namespace. Called once during engine initialization. */

  public static registerBuiltInClasses(): void {
    if (this.builtInClassesRegistered) {
      return;
    }

    if (1) {
      this.registerClass(Prefix.THREE, THREE.AmbientLight, 'AmbientLight');
      this.registerClass(Prefix.THREE, THREE.AnimationAction, 'AnimationAction');
      this.registerClass(Prefix.THREE, THREE.AnimationClip, 'AnimationClip');
      this.registerClass(Prefix.THREE, THREE.AnimationLoader, 'AnimationLoader');
      this.registerClass(Prefix.THREE, THREE.AnimationMixer, 'AnimationMixer');
      this.registerClass(Prefix.THREE, THREE.AnimationObjectGroup, 'AnimationObjectGroup');
      this.registerClass(Prefix.THREE, THREE.AnimationUtils, 'AnimationUtils');
      this.registerClass(Prefix.THREE, THREE.ArcCurve, 'ArcCurve');
      this.registerClass(Prefix.THREE, THREE.ArrayCamera, 'ArrayCamera');
      this.registerClass(Prefix.THREE, THREE.ArrowHelper, 'ArrowHelper');
      this.registerClass(Prefix.THREE, THREE.Audio, 'Audio');
      this.registerClass(Prefix.THREE, THREE.AudioAnalyser, 'AudioAnalyser');
      this.registerClass(Prefix.THREE, THREE.AudioListener, 'AudioListener');
      this.registerClass(Prefix.THREE, THREE.AudioLoader, 'AudioLoader');
      this.registerClass(Prefix.THREE, THREE.AxesHelper, 'AxesHelper');
      this.registerClass(Prefix.THREE, THREE.BatchedMesh, 'BatchedMesh');
      this.registerClass(Prefix.THREE, THREE.Bone, 'Bone');
      this.registerClass(Prefix.THREE, THREE.BooleanKeyframeTrack, 'BooleanKeyframeTrack');
      this.registerClass(Prefix.THREE, THREE.Box2, 'Box2');
      this.registerClass(Prefix.THREE, THREE.Box3, 'Box3');
      this.registerClass(Prefix.THREE, THREE.Box3Helper, 'Box3Helper');
      this.registerClass(Prefix.THREE, THREE.BoxGeometry, 'BoxGeometry');
      this.registerClass(Prefix.THREE, THREE.BoxHelper, 'BoxHelper');
      this.registerClass(Prefix.THREE, THREE.BufferAttribute, 'BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.BufferGeometry, 'BufferGeometry');
      this.registerClass(Prefix.THREE, THREE.BufferGeometryLoader, 'BufferGeometryLoader');
      this.registerClass(Prefix.THREE, THREE.Camera, 'Camera');
      this.registerClass(Prefix.THREE, THREE.CameraHelper, 'CameraHelper');
      this.registerClass(Prefix.THREE, THREE.CanvasTexture, 'CanvasTexture');
      this.registerClass(Prefix.THREE, THREE.CapsuleGeometry, 'CapsuleGeometry');
      this.registerClass(Prefix.THREE, THREE.CatmullRomCurve3, 'CatmullRomCurve3');
      this.registerClass(Prefix.THREE, THREE.CircleGeometry, 'CircleGeometry');
      this.registerClass(Prefix.THREE, THREE.Timer, 'Timer');
      this.registerClass(Prefix.THREE, THREE.Color, 'Color');
      this.registerClass(Prefix.THREE, THREE.ColorKeyframeTrack, 'ColorKeyframeTrack');
      this.registerClass(Prefix.THREE, THREE.CompressedArrayTexture, 'CompressedArrayTexture');
      this.registerClass(Prefix.THREE, THREE.CompressedCubeTexture, 'CompressedCubeTexture');
      this.registerClass(Prefix.THREE, THREE.CompressedTexture, 'CompressedTexture');
      this.registerClass(Prefix.THREE, THREE.CompressedTextureLoader, 'CompressedTextureLoader');
      this.registerClass(Prefix.THREE, THREE.ConeGeometry, 'ConeGeometry');
      this.registerClass(Prefix.THREE, THREE.CubeCamera, 'CubeCamera');
      this.registerClass(Prefix.THREE, THREE.CubeTexture, 'CubeTexture');
      this.registerClass(Prefix.THREE, THREE.CubeTextureLoader, 'CubeTextureLoader');
      this.registerClass(Prefix.THREE, THREE.CubicBezierCurve, 'CubicBezierCurve');
      this.registerClass(Prefix.THREE, THREE.CubicBezierCurve3, 'CubicBezierCurve3');
      this.registerClass(Prefix.THREE, THREE.CubicInterpolant, 'CubicInterpolant');
      this.registerClass(Prefix.THREE, THREE.CurvePath, 'CurvePath');
      this.registerClass(Prefix.THREE, THREE.CylinderGeometry, 'CylinderGeometry');
      this.registerClass(Prefix.THREE, THREE.Cylindrical, 'Cylindrical');
      this.registerClass(Prefix.THREE, THREE.Data3DTexture, 'Data3DTexture');
      this.registerClass(Prefix.THREE, THREE.DataArrayTexture, 'DataArrayTexture');
      this.registerClass(Prefix.THREE, THREE.DataTexture, 'DataTexture');
      this.registerClass(Prefix.THREE, THREE.DataTextureLoader, 'DataTextureLoader');
      this.registerClass(Prefix.THREE, THREE.DepthTexture, 'DepthTexture');
      this.registerClass(Prefix.THREE, THREE.DirectionalLight, 'DirectionalLight');
      this.registerClass(Prefix.THREE, THREE.DirectionalLightHelper, 'DirectionalLightHelper');
      this.registerClass(Prefix.THREE, THREE.DiscreteInterpolant, 'DiscreteInterpolant');
      this.registerClass(Prefix.THREE, THREE.DodecahedronGeometry, 'DodecahedronGeometry');
      this.registerClass(Prefix.THREE, THREE.EdgesGeometry, 'EdgesGeometry');
      this.registerClass(Prefix.THREE, THREE.EllipseCurve, 'EllipseCurve');
      this.registerClass(Prefix.THREE, THREE.Euler, 'Euler');
      this.registerClass(Prefix.THREE, THREE.EventDispatcher, 'EventDispatcher');
      this.registerClass(Prefix.THREE, THREE.ExtrudeGeometry, 'ExtrudeGeometry');
      this.registerClass(Prefix.THREE, THREE.FileLoader, 'FileLoader');
      this.registerClass(Prefix.THREE, THREE.Float16BufferAttribute, 'Float16BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Float32BufferAttribute, 'Float32BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Fog, 'Fog');
      this.registerClass(Prefix.THREE, THREE.FogExp2, 'FogExp2');
      this.registerClass(Prefix.THREE, THREE.FramebufferTexture, 'FramebufferTexture');
      this.registerClass(Prefix.THREE, THREE.Frustum, 'Frustum');
      this.registerClass(Prefix.THREE, THREE.GLBufferAttribute, 'GLBufferAttribute');
      this.registerClass(Prefix.THREE, THREE.GridHelper, 'GridHelper');
      this.registerClass(Prefix.THREE, THREE.Group, 'Group');
      this.registerClass(Prefix.THREE, THREE.HemisphereLight, 'HemisphereLight');
      this.registerClass(Prefix.THREE, THREE.HemisphereLightHelper, 'HemisphereLightHelper');
      this.registerClass(Prefix.THREE, THREE.IcosahedronGeometry, 'IcosahedronGeometry');
      this.registerClass(Prefix.THREE, THREE.ImageBitmapLoader, 'ImageBitmapLoader');
      this.registerClass(Prefix.THREE, THREE.ImageLoader, 'ImageLoader');
      this.registerClass(Prefix.THREE, THREE.ImageUtils, 'ImageUtils');
      this.registerClass(Prefix.THREE, THREE.InstancedBufferAttribute, 'InstancedBufferAttribute');
      this.registerClass(Prefix.THREE, THREE.InstancedBufferGeometry, 'InstancedBufferGeometry');
      this.registerClass(Prefix.THREE, THREE.InstancedInterleavedBuffer, 'InstancedInterleavedBuffer');
      this.registerClass(Prefix.THREE, THREE.InstancedMesh, 'InstancedMesh');
      this.registerClass(Prefix.THREE, THREE.Int16BufferAttribute, 'Int16BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Int32BufferAttribute, 'Int32BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Int8BufferAttribute, 'Int8BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.InterleavedBuffer, 'InterleavedBuffer');
      this.registerClass(Prefix.THREE, THREE.InterleavedBufferAttribute, 'InterleavedBufferAttribute');
      this.registerClass(Prefix.THREE, THREE.KeyframeTrack, 'KeyframeTrack');
      this.registerClass(Prefix.THREE, THREE.LOD, 'LOD');
      this.registerClass(Prefix.THREE, THREE.LatheGeometry, 'LatheGeometry');
      this.registerClass(Prefix.THREE, THREE.Layers, 'Layers');
      this.registerClass(Prefix.THREE, THREE.LightProbe, 'LightProbe');
      this.registerClass(Prefix.THREE, THREE.Line, 'Line');
      this.registerClass(Prefix.THREE, THREE.Line3, 'Line3');
      this.registerClass(Prefix.THREE, THREE.LineBasicMaterial, 'LineBasicMaterial');
      this.registerClass(Prefix.THREE, THREE.LineCurve, 'LineCurve');
      this.registerClass(Prefix.THREE, THREE.LineCurve3, 'LineCurve3');
      this.registerClass(Prefix.THREE, THREE.LineDashedMaterial, 'LineDashedMaterial');
      this.registerClass(Prefix.THREE, THREE.LineLoop, 'LineLoop');
      this.registerClass(Prefix.THREE, THREE.LineSegments, 'LineSegments');
      this.registerClass(Prefix.THREE, THREE.LinearInterpolant, 'LinearInterpolant');
      this.registerClass(Prefix.THREE, THREE.Loader, 'Loader');
      this.registerClass(Prefix.THREE, THREE.LoaderUtils, 'LoaderUtils');
      this.registerClass(Prefix.THREE, THREE.LoadingManager, 'LoadingManager');
      this.registerClass(Prefix.THREE, THREE.Material, 'Material');
      this.registerClass(Prefix.THREE, THREE.MaterialLoader, 'MaterialLoader');
      this.registerClass(Prefix.THREE, THREE.Matrix2, 'Matrix2');
      this.registerClass(Prefix.THREE, THREE.Matrix3, 'Matrix3');
      this.registerClass(Prefix.THREE, THREE.Matrix4, 'Matrix4');
      this.registerClass(Prefix.THREE, THREE.Mesh, 'Mesh');
      this.registerClass(Prefix.THREE, THREE.MeshBasicMaterial, 'MeshBasicMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshDepthMaterial, 'MeshDepthMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshDistanceMaterial, 'MeshDistanceMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshLambertMaterial, 'MeshLambertMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshMatcapMaterial, 'MeshMatcapMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshNormalMaterial, 'MeshNormalMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshPhongMaterial, 'MeshPhongMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshPhysicalMaterial, 'MeshPhysicalMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshStandardMaterial, 'MeshStandardMaterial');
      this.registerClass(Prefix.THREE, THREE.MeshToonMaterial, 'MeshToonMaterial');
      this.registerClass(Prefix.THREE, THREE.NumberKeyframeTrack, 'NumberKeyframeTrack');
      this.registerClass(Prefix.THREE, THREE.Object3D, 'Object3D');
      this.registerClass(Prefix.THREE, THREE.ObjectLoader, 'ObjectLoader');
      this.registerClass(Prefix.THREE, THREE.OctahedronGeometry, 'OctahedronGeometry');
      this.registerClass(Prefix.THREE, THREE.OrthographicCamera, 'OrthographicCamera');
      this.registerClass(Prefix.THREE, THREE.PMREMGenerator, 'PMREMGenerator');
      this.registerClass(Prefix.THREE, THREE.Path, 'Path');
      this.registerClass(Prefix.THREE, THREE.PerspectiveCamera, 'PerspectiveCamera');
      this.registerClass(Prefix.THREE, THREE.Plane, 'Plane');
      this.registerClass(Prefix.THREE, THREE.PlaneGeometry, 'PlaneGeometry');
      this.registerClass(Prefix.THREE, THREE.PlaneHelper, 'PlaneHelper');
      this.registerClass(Prefix.THREE, THREE.PointLight, 'PointLight');
      this.registerClass(Prefix.THREE, THREE.PointLightHelper, 'PointLightHelper');
      this.registerClass(Prefix.THREE, THREE.Points, 'Points');
      this.registerClass(Prefix.THREE, THREE.PointsMaterial, 'PointsMaterial');
      this.registerClass(Prefix.THREE, THREE.PolarGridHelper, 'PolarGridHelper');
      this.registerClass(Prefix.THREE, THREE.PolyhedronGeometry, 'PolyhedronGeometry');
      this.registerClass(Prefix.THREE, THREE.PositionalAudio, 'PositionalAudio');
      this.registerClass(Prefix.THREE, THREE.PropertyBinding, 'PropertyBinding');
      this.registerClass(Prefix.THREE, THREE.PropertyMixer, 'PropertyMixer');
      this.registerClass(Prefix.THREE, THREE.QuadraticBezierCurve, 'QuadraticBezierCurve');
      this.registerClass(Prefix.THREE, THREE.QuadraticBezierCurve3, 'QuadraticBezierCurve3');
      this.registerClass(Prefix.THREE, THREE.Quaternion, 'Quaternion');
      this.registerClass(Prefix.THREE, THREE.QuaternionKeyframeTrack, 'QuaternionKeyframeTrack');
      this.registerClass(Prefix.THREE, THREE.QuaternionLinearInterpolant, 'QuaternionLinearInterpolant');
      this.registerClass(Prefix.THREE, THREE.RawShaderMaterial, 'RawShaderMaterial');
      this.registerClass(Prefix.THREE, THREE.Ray, 'Ray');
      this.registerClass(Prefix.THREE, THREE.Raycaster, 'Raycaster');
      this.registerClass(Prefix.THREE, THREE.RectAreaLight, 'RectAreaLight');
      this.registerClass(Prefix.THREE, THREE.RenderTarget, 'RenderTarget');
      this.registerClass(Prefix.THREE, THREE.RenderTarget3D, 'RenderTarget3D');
      this.registerClass(Prefix.THREE, THREE.RingGeometry, 'RingGeometry');
      this.registerClass(Prefix.THREE, THREE.Scene, 'Scene');
      this.registerClass(Prefix.THREE, THREE.ShaderMaterial, 'ShaderMaterial');
      this.registerClass(Prefix.THREE, THREE.ShadowMaterial, 'ShadowMaterial');
      this.registerClass(Prefix.THREE, THREE.Shape, 'Shape');
      this.registerClass(Prefix.THREE, THREE.ShapeGeometry, 'ShapeGeometry');
      this.registerClass(Prefix.THREE, THREE.ShapePath, 'ShapePath');
      this.registerClass(Prefix.THREE, THREE.ShapeUtils, 'ShapeUtils');
      this.registerClass(Prefix.THREE, THREE.Skeleton, 'Skeleton');
      this.registerClass(Prefix.THREE, THREE.SkeletonHelper, 'SkeletonHelper');
      this.registerClass(Prefix.THREE, THREE.SkinnedMesh, 'SkinnedMesh');
      this.registerClass(Prefix.THREE, THREE.Source, 'Source');
      this.registerClass(Prefix.THREE, THREE.Sphere, 'Sphere');
      this.registerClass(Prefix.THREE, THREE.SphereGeometry, 'SphereGeometry');
      this.registerClass(Prefix.THREE, THREE.Spherical, 'Spherical');
      this.registerClass(Prefix.THREE, THREE.SphericalHarmonics3, 'SphericalHarmonics3');
      this.registerClass(Prefix.THREE, THREE.SplineCurve, 'SplineCurve');
      this.registerClass(Prefix.THREE, THREE.SpotLight, 'SpotLight');
      this.registerClass(Prefix.THREE, THREE.SpotLightHelper, 'SpotLightHelper');
      this.registerClass(Prefix.THREE, THREE.Sprite, 'Sprite');
      this.registerClass(Prefix.THREE, THREE.SpriteMaterial, 'SpriteMaterial');
      this.registerClass(Prefix.THREE, THREE.StereoCamera, 'StereoCamera');
      this.registerClass(Prefix.THREE, THREE.StringKeyframeTrack, 'StringKeyframeTrack');
      this.registerClass(Prefix.THREE, THREE.TetrahedronGeometry, 'TetrahedronGeometry');
      this.registerClass(Prefix.THREE, THREE.Texture, 'Texture');
      this.registerClass(Prefix.THREE, THREE.TextureLoader, 'TextureLoader');
      this.registerClass(Prefix.THREE, THREE.TorusGeometry, 'TorusGeometry');
      this.registerClass(Prefix.THREE, THREE.TorusKnotGeometry, 'TorusKnotGeometry');
      this.registerClass(Prefix.THREE, THREE.Triangle, 'Triangle');
      this.registerClass(Prefix.THREE, THREE.TubeGeometry, 'TubeGeometry');
      this.registerClass(Prefix.THREE, THREE.Uint16BufferAttribute, 'Uint16BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Uint32BufferAttribute, 'Uint32BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Uint8BufferAttribute, 'Uint8BufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Uint8ClampedBufferAttribute, 'Uint8ClampedBufferAttribute');
      this.registerClass(Prefix.THREE, THREE.Uniform, 'Uniform');
      this.registerClass(Prefix.THREE, THREE.UniformsGroup, 'UniformsGroup');
      this.registerClass(Prefix.THREE, THREE.Vector2, 'Vector2');
      this.registerClass(Prefix.THREE, THREE.Vector3, 'Vector3');
      this.registerClass(Prefix.THREE, THREE.Vector4, 'Vector4');
      this.registerClass(Prefix.THREE, THREE.VectorKeyframeTrack, 'VectorKeyframeTrack');
      this.registerClass(Prefix.THREE, THREE.VideoFrameTexture, 'VideoFrameTexture');
      this.registerClass(Prefix.THREE, THREE.VideoTexture, 'VideoTexture');
      this.registerClass(Prefix.THREE, THREE.WebGL3DRenderTarget, 'WebGL3DRenderTarget');
      this.registerClass(Prefix.THREE, THREE.WebGLArrayRenderTarget, 'WebGLArrayRenderTarget');
      this.registerClass(Prefix.THREE, THREE.WebGLCubeRenderTarget, 'WebGLCubeRenderTarget');
      this.registerClass(Prefix.THREE, THREE.WebGLRenderTarget, 'WebGLRenderTarget');
      this.registerClass(Prefix.THREE, THREE.WebGLRenderer, 'WebGLRenderer');
      this.registerClass(Prefix.THREE, THREE.WebGLUtils, 'WebGLUtils');
      this.registerClass(Prefix.THREE, THREE.WireframeGeometry, 'WireframeGeometry');
    } else {
      // enable this branch to log the code above...
      const lines = [];
      const blackList = ['AudioContext', 'Controls', 'Curve', 'DataUtils', 'Interpolant', 'Light', 'TextureUtils', 'WebXRController', 'createCanvasElement'];
      for (const [_, value] of Object.entries(THREE)) {
        if (isConstructor(value)) {
          let className = value.name;
          // weird that some three.js classes are exported with leading underscores, this fixes that
          if (className.startsWith('_')) {
            className = className.slice(1);
          }
          if (blackList.includes(className)) {
            continue;
          }
          lines.push(`this.registerClass(Prefix.THREE, THREE.${className}, '${className}');`);
        }
      }
      console.log(lines.join('\n'));
    }
    this.builtInClassesRegistered = true;
  }

  //====================================================================================================

  private static registerClass(prefix: Prefix, constructor: Constructor, className?: string): void {
    if (!isConstructor(constructor)) {
      throw new Error(`${constructor} is not a constructor`);
    }
    const fullName = `${prefix}${className ?? constructor.name}`;
    const existingConstructor = this.registry.get(fullName);
    if (existingConstructor === constructor) {
      return;
    }
    if (this.registry.has(fullName)) {
      throw new Error(`${fullName} class already registered`);
    }
    this.registry.set(fullName, constructor);
    this.reverseRegistry.set(constructor, fullName);
    // console.log(`class '${fullName}' registered`);
  }
}

/**
 * Options for the GameClass decorator.
 */
export interface GameClassOptions {
  /**
   * Whether this class should be treated as a resource.
   * Results in files named like: MyResource.{prefix}.json
   */
  isResource?: boolean;
  /**
   * The file prefix for this resource type (e.g., 'material', 'shader', 'sound').
   * Defaults to 'res' if not specified.
   */
  resourcePrefix?: string;
  /**
   * Whether this class is a serializable WebGPU node-material asset.
   * When true the class is listed in the NewMaterialDialog (unless
   * `excludeFromNewMaterialDialog`) and the property inspector uses @property
   * metadata to build its panel.
   */
  isNodeMaterialAsset?: boolean;
  /**
   * Human-readable display name shown in the editor (defaults to the class name).
   */
  nodeMaterialDisplayName?: string;
  /**
   * Optional grouping label for the editor combobox (e.g. 'FX Effects').
   */
  nodeMaterialGroup?: string;
  /**
   * When true with `isNodeMaterialAsset`, the class is omitted from the editor
   * "Create New Material" dialog (still serializable and editable when present).
   */
  excludeFromNewMaterialDialog?: boolean;
}

/**
 * Options for the EngineClass decorator.
 */
export interface EngineClassOptions {
  /**
   * Whether this class should be treated as a resource.
   * Results in files named like: MyResource.{prefix}.json
   */
  isResource?: boolean;
  /**
   * The file prefix for this resource type (e.g., 'material', 'shader', 'sound').
   * Defaults to 'res' if not specified.
   */
  resourcePrefix?: string;
  /**
   * Whether this class is a serializable WebGPU node-material asset.
   * When true the class is listed in the NewMaterialDialog (unless
   * `excludeFromNewMaterialDialog`) and the property inspector uses @property
   * metadata to build its panel.
   */
  isNodeMaterialAsset?: boolean;
  /**
   * Human-readable display name shown in the editor (defaults to the class name).
   */
  nodeMaterialDisplayName?: string;
  /**
   * Optional grouping label for the editor combobox (e.g. 'FX Effects').
   */
  nodeMaterialGroup?: string;
  /**
   * When true with `isNodeMaterialAsset`, the class is omitted from the editor
   * "Create New Material" dialog (still serializable and editable when present).
   */
  excludeFromNewMaterialDialog?: boolean;
}

/**
 * Decorator for registering a class as an engine component.
 * @param className - The name to register the class under
 */

export function EngineClass(className: string, options?: EngineClassOptions) {
  return function (constructor: Constructor) {
    ClassRegistry.registerEngineClass(constructor, className);
    if (options?.isResource) {
      ClassRegistry.markAsResource(constructor, options.resourcePrefix ?? 'res');
    }
    if (options?.isNodeMaterialAsset) {
      ClassRegistry.markAsNodeMaterialAsset(
        constructor,
        options.nodeMaterialDisplayName ?? className,
        'engine',
        options.nodeMaterialGroup,
        options.excludeFromNewMaterialDialog,
      );
    }
  };
}

/**
 * Decorator for registering an abstract engine class.
 * The class is added to the registry so it can be resolved for type-checking
 * (e.g. instanceof checks in the editor), but constructObject will throw if
 * someone tries to instantiate it directly.
 * @param className - The name to register the class under
 */
export function AbstractEngineClass(className: string) {
  return function (constructor: Function) {
    ClassRegistry.registerAbstractEngineClass(constructor as unknown as AbstractConstructor, className);
  };
}

/**
 * Decorator for registering a class as a game component.
 */

export function GameClass(options?: GameClassOptions) {
  return function (constructor: Constructor) {
    ClassRegistry.registerGameClass(constructor);
    if (options?.isResource) {
      ClassRegistry.markAsResource(constructor, options.resourcePrefix ?? 'res');
    }
    if (options?.isNodeMaterialAsset) {
      ClassRegistry.markAsNodeMaterialAsset(
        constructor,
        options.nodeMaterialDisplayName ?? constructor.name,
        'game',
        options.nodeMaterialGroup,
        options.excludeFromNewMaterialDialog,
      );
    }
  };
}

ClassRegistry.addClassesToRedirect({
  'ENGINE.GLTMeshActor': 'ENGINE.GLTFMeshActor',
  'ENGINE.BasicNodeMaterialAsset': 'ENGINE.MeshBasicNodeMaterialAsset',
  'ENGINE.LambertNodeMaterialAsset': 'ENGINE.MeshLambertNodeMaterialAsset',
  'ENGINE.PhongNodeMaterialAsset': 'ENGINE.MeshPhongNodeMaterialAsset',
  'ENGINE.StandardNodeMaterialAsset': 'ENGINE.MeshStandardNodeMaterialAsset',
  'ENGINE.PhysicalNodeMaterialAsset': 'ENGINE.MeshPhysicalNodeMaterialAsset',
  'ENGINE.ToonNodeMaterialAsset': 'ENGINE.MeshToonNodeMaterialAsset',
  'ENGINE.MatcapNodeMaterialAsset': 'ENGINE.MeshMatcapNodeMaterialAsset',
  'ENGINE.NormalNodeMaterialAsset': 'ENGINE.MeshNormalNodeMaterialAsset',
});
